import { useEffect, useState } from "react";
import { http, apiError } from "@/lib/api";
import { Flame } from "@phosphor-icons/react";

export default function ClientHabitGrid({ clientId }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await http.get(`/coach/clients/${clientId}/habits`);
        setData(data);
      } catch (e) { setErr(apiError(e)); }
    })();
  }, [clientId]);

  if (!data) return null;
  const habits = data.habits || [];
  if (habits.length === 0) {
    return (
      <div className="id-card" data-testid="coach-habit-grid-empty">
        <div className="id-label mb-2">Daily Habits</div>
        <p className="text-zinc-500 text-sm font-manrope">No habits set for this client yet. Add up to 5 in the Plan Editor.</p>
      </div>
    );
  }

  const last14 = data.last14 || [];
  // Build per-habit completion across 14 days requires per-habit detail; we have only per-day counts.
  // So we render a compact 14-day grid (overall) + the labelled habit list.

  return (
    <div className="id-card" data-testid="coach-habit-grid">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <div className="id-label mb-1">Habit Compliance · last 14 days</div>
          <div className="font-oswald text-2xl uppercase tracking-wide">{habits.length} habit{habits.length === 1 ? "" : "s"} tracked</div>
        </div>
        <div className="text-right">
          <div className="id-label text-zinc-500 mb-1 flex items-center gap-1 justify-end">
            <Flame size={12} weight="fill" /> Streak
          </div>
          <div className="font-oswald text-3xl text-white" data-testid="coach-habit-streak">{data.streak}</div>
          <div className="text-[10px] text-zinc-500 uppercase tracking-[0.2em] font-bold mt-1">day{data.streak === 1 ? "" : "s"}</div>
        </div>
      </div>

      {/* 14-day overall grid */}
      <div className="flex gap-1 mb-5" data-testid="coach-habit-history">
        {last14.map((d) => {
          const pct = d.total ? d.done / d.total : 0;
          const cls = pct === 1
            ? "bg-[#FF3333]"
            : pct >= 0.5 ? "bg-[#FF3333]/60"
            : pct > 0 ? "bg-[#FF3333]/25"
            : "bg-[#1A1A1A] border border-[#262626]";
          return <div key={d.date} className={`flex-1 h-8 ${cls}`} title={`${d.date} · ${d.done}/${d.total}`}></div>;
        })}
      </div>
      <div className="flex justify-between text-[10px] text-zinc-600 uppercase tracking-[0.15em] font-bold mb-5">
        <span>{last14[0]?.date?.slice(5) || ""}</span>
        <span>Today</span>
      </div>

      {/* Today's habits */}
      <div className="border-t border-[#262626] pt-4">
        <div className="id-label mb-3">Today · {data.date}</div>
        <ul className="space-y-2">
          {habits.map((h) => {
            const done = !!data.completed?.[h.id];
            return (
              <li key={h.id} className={`flex items-center justify-between px-4 py-2 border ${done ? "border-[#FF3333]/40 bg-[#FF3333]/5" : "border-[#262626] bg-[#0F0F0F]"}`}>
                <span className="font-manrope text-sm">{h.label}</span>
                <span className={`id-label ${done ? "text-[#FF3333]" : "text-zinc-500"}`}>{done ? "✓ Done" : "Pending"}</span>
              </li>
            );
          })}
        </ul>
      </div>

      {err && <div className="mt-3 border border-[#FF3333] bg-[#FF3333]/10 px-3 py-2 text-xs text-[#FF3333]">{err}</div>}
    </div>
  );
}
